/**
 * `PWA-041`/`PWA-042`/`PWA-043`/`PWA-044`/`PWA-046`. Rendered above `<main>`
 * in `App.tsx`, the same "boot has not decided which screen is about to
 * render yet" placement `SaveNotice.tsx` already uses for the same reason:
 * this notice is not about any one screen, and outliving every one of them
 * is the point.
 *
 * `PWA-046` ("shall not obscure any interactive control") is why this is an
 * ordinary flow element pushing `<main>` down, not a fixed overlay drawn on
 * top of it -- a `position: fixed` banner can only ever be checked screen by
 * screen for what it happens not to cover today; a flow element structurally
 * cannot cover anything, on any screen, including ones added later. `PWA-046`
 * stays `Manual` regardless (SDD-005) -- this is the design choice that makes
 * that verification a formality rather than a real risk, not a substitute for
 * doing it.
 */

import { useEffect } from 'preact/hooks';

import { announce } from '@platform/announce';
import { applyUpdate, dismissUpdate, updateAvailable, updateDismissed } from '@platform/pwa';
import { levelInProgressSignal } from '@shell/level-in-progress';

// Not `role="status"` -- an implicit live region of its own, which
// `platform/announce.ts`'s own header warns produces overlapping or dropped
// announcements alongside the shared one this component already calls
// `announce()` through. `SaveNotice.tsx` makes the identical choice for the
// identical reason.

export function UpdateNotice() {
  const visible = updateAvailable.value && !updateDismissed.value && !levelInProgressSignal.value;

  // `visible` alone, not `updateAvailable.value`: re-entering visibility
  // after a level ends (`PWA-042`'s own gate lifting) is a genuine
  // re-announcement a screen-reader user benefits from, the same as the
  // first one -- unlike `platform/announce.ts`'s own coalescing, which is
  // about several calls within one throttle window, not about a notice
  // becoming visible again after being legitimately hidden for minutes.
  useEffect(() => {
    if (visible) announce('A new version of Puzzle Gate is available.');
  }, [visible]);

  if (!visible) return null;

  return (
    <div class="update-notice">
      <p>A new version is available.</p>
      <div class="update-notice__actions">
        <button type="button" onClick={applyUpdate}>
          Update now
        </button>
        <button type="button" onClick={dismissUpdate}>
          Not now
        </button>
      </div>
    </div>
  );
}
